/**
 * Agent-side Twilio Voice integration. Mounted ONCE at the app root inside the
 * auth guard, but the Twilio `Device` is created LAZILY, per call:
 *
 *   - Dashboard CALL LEAD → /call-center/call-lead/{sessionName} route.
 *   - Direct call from anywhere (lead-info sidebar, lead profile, deal, etc.) → CallLeadPopup modal.
 *
 * Why lazy: the Voice token is now lead-scoped — `POST /voice/token` takes a
 * `{ leadId }` and returns `{ identity, token, sessionName }`. There's no lead
 * to mint a token for at provider-mount time, so the Device can only be built
 * once the agent actually starts a call. `connect()` does the full sequence:
 *   1) fetch the lead-scoped token,
 *   2) (re)create + register the Device with it,
 *   3) `device.connect({ params: { To, sessionName } })`.
 * The backend-returned `sessionName` (NOT a caller-supplied one) is what binds
 * the call to the lead's session lifecycle. Both entry points are therefore
 * session-linked now.
 *
 * Socket usage here is INBOUND only:
 *
 *   "call.ended"        IN  — listened to per-call. Backend emits this from
 *                        the Twilio dial-action statusCallback when the PSTN
 *                        leg disconnects. Robust signal we use to terminate
 *                        the local UI without waiting for the Voice SDK's
 *                        own (sometimes-delayed) `disconnect` event.
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Device, Call } from "@twilio/voice-sdk";
import * as Sentry from "@sentry/react";
import { fetchTwilioAccessToken } from "@/services/call-center/twilio.token.service";
import { useSocketContext } from "@/context/socket.context/socket.context";

export type TwilioCallState =
  | "idle"
  | "initializing"
  | "ready"
  | "connecting"
  | "ringing"
  | "in-call"
  | "ended"
  | "error";

/**
 * Why a call ended, set on the terminal transition and surfaced via the
 * context for consumers that need it.
 */
export type CallEndReason = "completed" | "no-answer" | "rejected" | "failed";

/**
 * Agent-facing copy for each terminal reason. Centralized so the dedicated
 * call screen and the popup show the same wording — the whole point of
 * tracking the reason is that "the lead declined" reads differently from a
 * normal hangup.
 */
export const CALL_END_REASON_LABELS: Record<CallEndReason, string> = {
  completed: "Call ended",
  "no-answer": "No answer",
  rejected: "Lead declined the call",
  failed: "Call failed",
};

/**
 * Per-call context passed when starting an outbound call.
 *
 * `leadId`: REQUIRED. Used to mint the lead-scoped Voice token — the backend
 *   resolves/creates the call session from it and returns the `sessionName`
 *   we dial with. There is no token (and therefore no call) without it.
 *
 * The dialed `sessionName` comes back from the token response (not an input)
 * and is used internally for the dial params and the inbound `call.ended` match.
 */
export type ConnectOptions = {
  phoneNumber: string;
  leadId: number;
  /**
   * Display name shown by the floating ActiveCallIndicator when the agent
   * navigates away from the originating call surface. Optional but strongly
   * recommended — without it the indicator falls back to the phoneNumber.
   */
  leadName?: string;
  /**
   * Route path that originated the call (e.g. `/call-center/call-lead/{id}`).
   * The floating ActiveCallIndicator surfaces a "Return to call" button that
   * navigates here. Omit for popup-driven calls — the popup is its own
   * persistent indicator and doesn't need a return-to path.
   */
  originPath?: string;
};

type TwilioContextProps = {
  state: TwilioCallState;
  errorMessage: string | null;
  /** True when the last (or current) error was caused by a lost internet connection. */
  isNetworkError: boolean;
  isMuted: boolean;
  /** True when the agent has placed the lead on hold. The mic is muted while on hold. */
  isOnHold: boolean;
  callStartedAt: Date | null;
  /**
   * True whenever a call is actively in progress — from the moment connect()
   * starts setting up (initializing the lead-scoped token + Device) through
   * connecting, ringing, and in-call. Used by call-trigger buttons across the
   * app to disable themselves so the agent can't start a second call while one
   * is live.
   */
  isCallActive: boolean;
  /**
   * Why the last call ended, set on the terminal transition (null until then,
   * cleared when a new call starts).
   */
  callEndReason: CallEndReason | null;
  /** Display name for the active call's lead, used by the floating indicator. */
  leadName: string | null;
  /** E.164 phone for the active call, fallback display when leadName absent. */
  phoneNumber: string | null;
  /** Origin route to navigate back to via the indicator's Return button. */
  originPath: string | null;
  /** leadId for the active call's lead — used by the indicator to pass state when returning. */
  leadId: number | null;
  /** Twilio session name for the active call — used by the indicator to pass state when returning. */
  sessionName: string | null;
  connect: (opts: ConnectOptions) => Promise<void>;
  mute: () => void;
  unmute: () => void;
  /** Place the lead on hold: mutes the agent's mic so the lead hears silence. */
  hold: () => void;
  /** Take the lead off hold and restore audio. */
  unhold: () => void;
  hangup: () => void;
  /**
   * Reset state back to "idle" after a terminal call so the next call starts
   * fresh. Lets a UI surface close itself (popup, dedicated route) and free
   * the slot for the next call.
   */
  resetCallState: () => void;
};

const TwilioContext = createContext<TwilioContextProps | null>(null);

// Console prefix for the warn-level logs we keep in production (quality
// warnings, ignored-duplicate-terminal events, audio-constraints failure).
// Useful when an agent screen-shares their console to debug a call issue.
const LOG = "[Twilio]";

export const TwilioProvider = ({ children }: PropsWithChildren) => {
  const socket = useSocketContext();
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // Synchronous guard against duplicate `connect()` invocations (e.g. user
  // double-clicks "Start Call"). React state alone is too slow — the second
  // click fires before the first `setState("connecting")` has rendered. The
  // ref is set/cleared inside connect()'s lifecycle so any concurrent call
  // sees it before its own setState.
  const callInFlightRef = useRef(false);
  // Holds the teardown for the per-call `socket.on("call.ended", ...)`
  // listener so finishCall() AND the provider's unmount cleanup can both
  // remove it. Without this, a connect()→unmount path (e.g. agent navigates
  // away mid-call) would leak a listener bound to the closed call's state.
  const cleanupSocketRef = useRef<(() => void) | null>(null);
  // Mirror of isMuted state as a ref so hold() / unhold() can read the live
  // mute value without a stale closure — the mute event handler keeps it in sync.
  const currentMutedRef = useRef(false);
  // Mute state captured at the moment hold() is invoked, restored by unhold()
  // so an agent who was already muted before going on hold stays muted after.
  const preHoldMutedRef = useRef(false);

  // When true, the terminal "error" was caused by a lost internet connection.
  // Cleared by resetCallState() so the next call starts with a clean flag.
  const networkTerminatedRef = useRef(false);

  const [state, setState] = useState<TwilioCallState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<Date | null>(null);
  const [callEndReason, setCallEndReason] = useState<CallEndReason | null>(null);
  // Per-call identity surfaced to the global indicator. Cleared by
  // resetCallState() so a stale lead name doesn't show up on the next call.
  const [activeLeadName, setActiveLeadName] = useState<string | null>(null);
  const [activePhoneNumber, setActivePhoneNumber] = useState<string | null>(null);
  const [activeOriginPath, setActiveOriginPath] = useState<string | null>(null);
  const [activeLeadId, setActiveLeadId] = useState<number | null>(null);
  const [activeSessionName, setActiveSessionName] = useState<string | null>(null);

  // Provider-unmount cleanup: if the provider unmounts mid-call (e.g. agent
  // logs out), tear down the live call, device, and the per-call socket
  // listener so nothing leaks.
  useEffect(() => {
    return () => {
      if (cleanupSocketRef.current) {
        cleanupSocketRef.current();
        cleanupSocketRef.current = null;
      }
      if (callRef.current) {
        try { callRef.current.disconnect(); } catch { /* noop */ }
        callRef.current = null;
      }
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }
    };
  }, []);

  // Monitor internet connectivity during an active call. When the browser
  // fires "offline", we flag the termination as network-driven so finishCall
  // (triggered by the subsequent Twilio `disconnect` event) surfaces the error
  // state with a friendly message instead of the generic "Call ended" outcome.
  useEffect(() => {
    const handleOffline = () => {
      // Only act when a call is live. If no Call object exists yet (e.g. still
      // minting the token), the connect() catch block will detect offline state
      // on its own and show the same friendly message.
      if (!callRef.current || !callInFlightRef.current) return;
      networkTerminatedRef.current = true;
      setIsNetworkError(true);
      setErrorMessage("No internet connection. Your call has been disconnected.");
      // Trigger the Twilio disconnect path — finishCall (via the call's
      // "disconnect" event) will pick up networkTerminatedRef and transition to
      // "error" instead of the normal "ended" state.
      try { callRef.current.disconnect(); } catch { /* noop */ }
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, []);

  const connect = useCallback(async (opts: ConnectOptions) => {
    // Synchronous dedup. Cleared by the call's terminal events (disconnect,
    // cancel, reject, error) or the catch block below if connect() throws
    // before a Call object exists.
    if (callInFlightRef.current) return;
    callInFlightRef.current = true;

    // Capture per-call context into closure-stable consts for the event
    // handlers below. They reference these instead of the provider-level
    // refs so the call's identity is fixed at dial-time even if a parallel
    // call attempt mutates state. `sessionName` is filled in from the token
    // response once we have it.
    const { phoneNumber, leadId, leadName, originPath } = opts;
    let sessionName = "";

    try {
      setErrorMessage(null);
      setIsNetworkError(false);
      setIsMuted(false);
      setIsOnHold(false);
      setCallStartedAt(null);
      setCallEndReason(null);
      currentMutedRef.current = false;
      preHoldMutedRef.current = false;
      // "initializing" while we mint the token + register the Device, then
      // "connecting" once we actually dial (below).
      setState("initializing");
      // Surface call identity to the global indicator. Defaults to the raw
      // phone number when no leadName was provided so we never render a
      // blank widget.
      setActiveLeadName(leadName ?? null);
      setActivePhoneNumber(phoneNumber);
      setActiveOriginPath(originPath ?? null);
      setActiveLeadId(leadId);

      Sentry.addBreadcrumb({
        category: "twilio.device",
        level: "info",
        message: "Requesting lead-scoped Voice token",
        data: { leadId, phoneNumber },
      });

      // 1) Mint the lead-scoped token. The backend resolves/creates the call
      //    session and returns the `sessionName` we dial with.
      const tokenResponse = await fetchTwilioAccessToken(leadId);
      sessionName = tokenResponse.sessionName;
      setActiveSessionName(sessionName || null);
      const isLinkedToSession = !!sessionName;

      // 2) (Re)create the Device with the freshly-minted token. Destroy any
      //    stale device from a prior call first so we never stack registered
      //    devices.
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }

      // ─── Audio quality tuning ─────────────────────────────────────────
      //
      // 1) `codecPreferences: [Opus, PCMU]` — Opus is wider band and has
      //    much better packet-loss concealment than the default PCMU
      //    (G.711). The PSTN leg still transcodes to PCMU at Twilio, so
      //    the lead doesn't hear wideband audio, but the WebRTC leg
      //    (agent ↔ Twilio) becomes much more resilient to jitter / loss,
      //    which is usually where the perceptual quality drop comes from.
      //
      // 2) `maxAverageBitrate: 16000` — bumps Opus from its default ~8 kbps
      //    voice floor. 16 kbps is the sweet spot for outbound voice: enough
      //    headroom for clean encoding without flooding the uplink.
      //
      // 3) `edge: "roaming"` — explicit default. Twilio auto-selects the
      //    closest edge from this list; switch to a specific one like
      //    `"ashburn"` if you can measure better latency to a particular
      //    region from your typical agent location.
      const device = new Device(tokenResponse.token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        maxAverageBitrate: 16000,
        edge: "roaming",
        // Without this flag, a gateway HANGUP that carries a SIP-level cause
        // (31603 Decline, 31486 Busy, 31480 No Answer...) gets collapsed into
        // the generic ConnectionError 31005, hiding the real outcome. With it,
        // the SDK surfaces the precise error so the `error` handler below can
        // tell "lead declined" apart from an actual connection failure.
        enableImprovedSignalingErrorPrecision: true,
      });

      // Force the browser's getUserMedia constraints to enable echo
      // cancellation, noise suppression, and auto gain control. Most
      // browsers default these to ON for voice, but being explicit avoids
      // surprises when a user has overridden defaults via OS audio drivers
      // or a Bluetooth headset that exposes the raw mic. Fire-and-forget
      // — failures don't block device init (we just lose the tuning).
      device.audio?.setAudioConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }).catch((err) => {
        console.warn(`${LOG} setAudioConstraints failed`, err);
      });

      device.on("error", (err: { message?: string; code?: number }) => {
        Sentry.captureException(
          err instanceof Error ? err : new Error(`[Twilio Device] ${err?.message ?? "Unknown device error"}`),
          {
            tags: { component: "TwilioContext", source: "Device.on(error)" },
            extra: { twilioCode: err?.code, message: err?.message },
          },
        );
        setErrorMessage(err?.message ?? "Device error");
        setState("error");
      });

      // Token refresh reuses THIS call's leadId — the token is lead-scoped,
      // so there's no generic token to fall back on.
      device.on("tokenWillExpire", async () => {
        Sentry.addBreadcrumb({
          category: "twilio.device",
          level: "info",
          message: "Token will expire — refreshing",
        });
        try {
          const refreshed = await fetchTwilioAccessToken(leadId);
          device.updateToken(refreshed.token);
        } catch (e) {
          Sentry.captureException(e, {
            tags: { component: "TwilioContext", operation: "tokenRefresh" },
          });
        }
      });

      await device.register();
      deviceRef.current = device;

      // 3) Dial.
      setState("connecting");
      Sentry.addBreadcrumb({
        category: "twilio.call",
        level: "info",
        message: "Initiating outbound call",
        data: { phoneNumber, sessionName, leadId, isLinkedToSession },
      });

      // The backend-returned `sessionName` ties the call to the lead's
      // session so the post-dial status callback can drive the lead's
      // IncomingCallScreen via WS events.
      const call = await device.connect({
        params: {
          To: phoneNumber,
          sessionName,
        },
      });

      callRef.current = call;

      // ─── Terminal-state guard ─────────────────────────────────────────
      // Centralizes the cleanup so every path that ends a call (disconnect,
      // cancel, reject, error, or the backend telling us via socket) goes
      // through the SAME sequence and is idempotent. The guard means even
      // if Twilio fires `disconnect` twice (or both `cancel` and `disconnect`
      // race), the socket emit and state transition only happen once.
      // Duplicate terminal events after the first are surfaced as a warn so
      // they don't go silent.
      let terminated = false;
      const finishCall = (
        reason: "completed" | "no-answer" | "rejected" | "failed",
        nextState: "ended" | "error",
        source: string,
      ) => {
        if (terminated) {
          console.warn(`${LOG} call.${source} fired AFTER terminal (ignored)`);
          return;
        }
        terminated = true;
        // Tear down the backend-driven listener so we don't leak it (and so
        // a future call doesn't accidentally inherit a stale handler).
        socket.off("call.ended", onCallEndedFromBackend);
        cleanupSocketRef.current = null;
        // Network-loss terminations always surface as an error regardless of
        // what Twilio reports. The `disconnect` event fires with reason
        // "completed" after we explicitly disconnect on offline, but the real
        // cause is the lost connection, so we override both the reason and
        // the next state here. Reset the flag so the next call starts clean.
        const effectiveReason = networkTerminatedRef.current ? "failed" : reason;
        const effectiveNextState = networkTerminatedRef.current ? "error" : nextState;
        // When the call ended due to network loss, destroy the Device now so
        // it cannot attempt to re-register when connectivity returns. Without
        // this, the Device re-connects to Twilio's signaling server, the stale
        // token is rejected, and device.on("error") fires with
        // "AccessTokenInvalid (20101)" — overwriting the friendly "No internet"
        // error message we already set.
        if (networkTerminatedRef.current && deviceRef.current) {
          try { deviceRef.current.destroy(); } catch { /* noop */ }
          deviceRef.current = null;
        }
        networkTerminatedRef.current = false;
        // Record WHY the call ended so consumers can read it off the context.
        setCallEndReason(effectiveReason);
        callRef.current = null;
        callInFlightRef.current = false;
        setState(effectiveNextState);
      };

      // ─── Backend-driven terminal event ────────────────────────────────
      // The Twilio Voice SDK does NOT reliably fire `disconnect` when the
      // PSTN side hangs up (the `<Dial>` answerOnBridge path inside Twilio's
      // bridge does not always propagate the BYE back to the WebRTC client).
      // The backend works around this by registering a TwiML `<Dial action>`
      // statusCallback — when the dialed leg ends, the backend emits this
      // socket event so we can close the call from the agent's side too.
      //
      // The payload carries `sessionName`; we only act when it matches the
      // currently active call so a stale event from a previous call can't
      // terminate this one.
      const onCallEndedFromBackend = (payload?: { sessionName?: string; reason?: string }) => {
        if (!isLinkedToSession) return;
        if (payload?.sessionName && payload.sessionName !== sessionName) return;
        // "rejected" comes through when the backend can tell the lead
        // declined; "voicemail" (pending backend AMD support) means the
        // carrier diverted a declined/unanswered call to voicemail — the
        // lead never picked up, so surface it as no-answer.
        const mapped: CallEndReason =
          payload?.reason === "no-answer" ||
          payload?.reason === "failed" ||
          payload?.reason === "rejected"
            ? payload.reason
            : payload?.reason === "voicemail"
              ? "no-answer"
              : "completed";
        finishCall(mapped, mapped === "failed" ? "error" : "ended", "socket:call.ended");
      };
      socket.on("call.ended", onCallEndedFromBackend);
      cleanupSocketRef.current = () => socket.off("call.ended", onCallEndedFromBackend);

      call.on("ringing", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call ringing on remote end",
          data: { sessionName, leadId },
        });
        setState("ringing");
      });

      call.on("accept", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call accepted (lead picked up)",
          data: { sessionName, leadId },
        });
        setCallStartedAt(new Date());
        setState("in-call");
      });

      // `disconnect` is the canonical event for call-end from EITHER side
      // (remote PSTN hangup, agent hangup, network drop after accept).
      // Twilio Voice SDK v2 fires it on the Call instance.
      call.on("disconnect", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call disconnected (completed)",
          data: { sessionName, leadId },
        });
        finishCall("completed", "ended", "disconnect");
      });

      call.on("cancel", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call cancelled (no answer)",
          data: { sessionName, leadId },
        });
        finishCall("no-answer", "ended", "cancel");
      });

      // ─── Error vs. normal-outcome triage ──────────────────────────────
      // For OUTBOUND calls the SDK never fires `reject` — a remote decline
      // arrives as a gateway HANGUP with a SIP cause code that surfaces
      // through THIS handler. Those are normal call outcomes, not failures:
      //   31603 Decline                → lead rejected the call
      //   31486 Busy Here              → lead is on another call
      //   31480 Temporarily Unavailable / 31487 Request Terminated → no answer
      // We read the code from the error itself and, while the improved
      // precision flag rolls out, fall back to `originalError.code` (the raw
      // gateway payload the generic 31005 wraps).
      //
      // TODO(contract): "rejected" isn't in the agent:disengaged contract's
      // enum yet (allowed: completed | denied | no-answer | failed | left).
      // Pending alignment with backend on whether to extend the enum or map
      // it to "denied".
      const HUMAN_HANGUP_CODES: Record<number, CallEndReason> = {
        31603: "rejected",
        31486: "rejected",
        31480: "no-answer",
        31487: "no-answer",
      };
      call.on(
        "error",
        (err: { message?: string; code?: number; originalError?: { code?: number } }) => {
          const causeCode = HUMAN_HANGUP_CODES[err?.code ?? -1] !== undefined
            ? err?.code
            : err?.originalError?.code;
          const humanReason = causeCode != null ? HUMAN_HANGUP_CODES[causeCode] : undefined;
          if (humanReason) {
            Sentry.addBreadcrumb({
              category: "twilio.call",
              level: "info",
              message: `Call ended by remote cause ${causeCode} (${humanReason})`,
              data: { sessionName, leadId, twilioCode: causeCode },
            });
            finishCall(humanReason, "ended", `error:${causeCode}`);
            return;
          }
          Sentry.captureException(
            err instanceof Error ? err : new Error(`[Twilio Call] ${err?.message ?? "Unknown call error"}`),
            {
              tags: { component: "TwilioContext", source: "Call.on(error)" },
              extra: { sessionName, leadId, phoneNumber, twilioCode: err?.code, message: err?.message },
            },
          );
          setErrorMessage(err?.message ?? "Call error");
          finishCall("failed", "error", "error");
        },
      );

      call.on("mute", (muted: boolean) => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: muted ? "Agent muted the call" : "Agent unmuted the call",
          data: { sessionName, leadId },
        });
        setIsMuted(muted);
        currentMutedRef.current = muted;
      });

      // ─── Quality warnings ─────────────────────────────────────────────
      // The Voice SDK continuously samples jitter, RTT, packet loss, audio
      // levels, and MOS. It fires `warning` when a metric crosses a bad
      // threshold and `warning-cleared` when it recovers. We log warnings
      // to the console so an agent screen-sharing their dev tools can show
      // them in real time, and breadcrumb to Sentry so they surface in any
      // post-hoc error report.
      //
      // Common warnings: high-jitter, high-rtt, high-packet-loss-fraction,
      // low-mos, low-bytes-received, low-audio-level, constant-audio-input-level.
      call.on("warning", (warningName: string, warningData?: unknown) => {
        console.warn(`${LOG} call.warning: ${warningName}`, warningData);
        Sentry.addBreadcrumb({
          category: "twilio.call.quality",
          level: "warning",
          message: `Quality warning: ${warningName}`,
          data: { sessionName, leadId, warningData },
        });
      });
      call.on("warning-cleared", (warningName: string) => {
        Sentry.addBreadcrumb({
          category: "twilio.call.quality",
          level: "info",
          message: `Quality warning cleared: ${warningName}`,
          data: { sessionName, leadId },
        });
      });
    } catch (err) {
      // Surface a friendly message when we know the failure is connectivity:
      // navigator.onLine === false is a reliable signal here because a network
      // error during token fetch or device.register() is almost always a dropped
      // connection, and both signals agree.
      const offline = !navigator.onLine;
      const message = offline
        ? "No internet connection. Please check your network and try again."
        : err instanceof Error ? err.message : "Failed to start call";
      Sentry.captureException(err, {
        tags: { component: "TwilioContext", operation: "connect", offline: String(offline) },
        extra: { sessionName, leadId, phoneNumber },
      });
      // Tear down any device we managed to create before the failure (token
      // fetch, register, or dial could each throw) so a retry starts clean.
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }
      // Release the in-flight guard so the agent can retry. We never got a
      // Call object back from Twilio, so there are no call event handlers
      // that would clear the ref for us.
      callInFlightRef.current = false;
      if (offline) setIsNetworkError(true);
      setErrorMessage(message);
      setCallEndReason("failed");
      setState("error");
    }
  }, [socket]);

  const mute = useCallback(() => {
    callRef.current?.mute(true);
  }, []);

  const unmute = useCallback(() => {
    callRef.current?.mute(false);
  }, []);

  const hold = useCallback(() => {
    // Save the agent's current mute state so unhold() can restore it. Without
    // this, an agent who was already muted before holding gets unexpectedly
    // unmuted when they resume the call.
    preHoldMutedRef.current = currentMutedRef.current;
    callRef.current?.mute(true);
    setIsOnHold(true);
  }, []);

  const unhold = useCallback(() => {
    // Restore the mute state the agent had before placing the lead on hold.
    callRef.current?.mute(preHoldMutedRef.current);
    setIsOnHold(false);
  }, []);

  const hangup = useCallback(() => {
    callRef.current?.disconnect();
  }, []);

  // After a terminal state ("ended" / "error"), call surfaces close themselves
  // (popup unmounts, route navigates away) and invoke resetCallState to drop
  // back to "idle" so the next call can start fresh.
  //
  // We deliberately do NOT destroy the per-call Device here. The popup's
  // handleClose calls hangup() then resetCallState() synchronously; the Call's
  // `disconnect` event (which runs finishCall — clearing the in-flight guard
  // and setting the terminal state) fires asynchronously AFTER. Destroying the
  // Device synchronously could pre-empt that event and strand the call as
  // in-flight. The lingering idle Device is torn down safely by the next
  // connect()'s stale-device cleanup or on provider unmount.
  const resetCallState = useCallback(() => {
    setState((prev) => (prev === "ended" || prev === "error" ? "idle" : prev));
    setErrorMessage(null);
    setIsNetworkError(false);
    setIsMuted(false);
    setIsOnHold(false);
    setCallStartedAt(null);
    setCallEndReason(null);
    setActiveLeadName(null);
    setActivePhoneNumber(null);
    setActiveOriginPath(null);
    setActiveLeadId(null);
    setActiveSessionName(null);
    currentMutedRef.current = false;
    preHoldMutedRef.current = false;
  }, []);

  const isCallActive =
    state === "initializing" ||
    state === "connecting" ||
    state === "ringing" ||
    state === "in-call";

  return (
    <TwilioContext.Provider
      value={{
        state,
        errorMessage,
        isNetworkError,
        isMuted,
        isOnHold,
        callStartedAt,
        isCallActive,
        callEndReason,
        leadName: activeLeadName,
        phoneNumber: activePhoneNumber,
        originPath: activeOriginPath,
        leadId: activeLeadId,
        sessionName: activeSessionName,
        connect,
        mute,
        unmute,
        hold,
        unhold,
        hangup,
        resetCallState,
      }}
    >
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilioContext = () => {
  const ctx = useContext(TwilioContext);
  if (!ctx) throw new Error("useTwilioContext must be used inside TwilioProvider");
  return ctx;
};
